import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    requestAnimationFrame(() => {
      this.#syncThumbnailRailsFromSlideshow();
      const selectionDeferred = this.#applyPendingGalleryMediaSelection();
      if (!selectionDeferred) {
        requestAnimationFrame(() => this.#scrollRailsToCurrentSlide('smooth'));
      }
    });

    queueMicrotask(() => this.#setupVerticalRailHeightObserver());
  }

  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #verticalRailResizeObserver = null;

  disconnectedCallback() {
    this.#verticalRailResizeObserver?.disconnect();
    this.#verticalRailResizeObserver = null;

    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Match vertical thumbnail column to the visible product media box (tighter than slideshow-container).
   */
  #syncVerticalThumbnailRailHeight() {
    const host = this.querySelector('.product-media-gallery__slideshow-host');
    const col = this.querySelector('.product-media-gallery__desktop-thumbnails-column');
    if (!(host instanceof HTMLElement) || !(col instanceof HTMLElement)) return;

    const visibleSlide = host.querySelector('slideshow-slide[aria-hidden="false"]');
    const mediaBox = visibleSlide?.querySelector('.product-media-container');
    const slidesScroller = host.querySelector('slideshow-slides');
    const container = host.querySelector('slideshow-container');

    /** @type {HTMLElement | null} */
    let target = null;
    if (mediaBox instanceof HTMLElement) target = mediaBox;
    else if (visibleSlide instanceof HTMLElement) target = visibleSlide;
    else if (slidesScroller instanceof HTMLElement) target = slidesScroller;
    else if (container instanceof HTMLElement) target = container;
    else target = host;

    let h = Math.max(0, Math.round(target.getBoundingClientRect().height));
    if (h < 1) return;

    /* Trim a few px so the rail does not read taller than the photo area (subpixel / borders). */
    h = Math.max(1, h - 6);

    col.style.maxHeight = `${h}px`;
  }

  #setupVerticalRailHeightObserver() {
    this.#verticalRailResizeObserver?.disconnect();
    this.#verticalRailResizeObserver = null;

    const host = this.querySelector('.product-media-gallery__slideshow-host');
    if (!(host instanceof HTMLElement)) return;

    const toWatch = [
      host.querySelector('slideshow-slides'),
      host.querySelector('slideshow-container'),
      host.querySelector('slideshow-component'),
    ].filter((el) => el instanceof HTMLElement);

    this.#verticalRailResizeObserver = new ResizeObserver(() => this.#syncVerticalThumbnailRailHeight());
    for (const el of toWatch.length > 0 ? toWatch : [host]) {
      this.#verticalRailResizeObserver.observe(el);
    }

    this.#syncVerticalThumbnailRailHeight();
    requestAnimationFrame(() => this.#syncVerticalThumbnailRailHeight());
  }

  /**
   * Section or dialog that receives variant updates (matches listener target).
   * @returns {HTMLElement | null}
   */
  #galleryMountRoot() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * Desktop thumbnail strip under the main carousel.
   * @returns {HTMLElement[]}
   */
  #thumbnailRailStrips() {
    const strip = this.querySelector('[data-desktop-product-thumbnails]');
    return strip instanceof HTMLElement ? [strip] : [];
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Jumps the main carousel from the desktop thumbnail strip (`on:click` on media-gallery).
   *
   * @param {number} index
   * @param {Event} [event]
   */
  selectSlide(index, event) {
    this.refs.slideshow?.select(index, event);
  }

  /**
   * @param {HTMLElement} track
   * @param {'previous' | 'next'} direction
   */
  #scrollThumbnailTrackBy(track, direction) {
    const vertical = track.classList.contains('product-media-gallery__desktop-thumbnails-track--vertical');

    if (vertical) {
      const amount = Math.max(80, Math.floor(track.clientHeight * 0.45));
      const delta = direction === 'previous' ? -amount : amount;
      track.scrollBy({
        top: delta,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      return;
    }

    const amount = Math.max(120, Math.floor(track.clientWidth * 0.55));
    const delta = direction === 'previous' ? -amount : amount;

    track.scrollBy({
      left: delta,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * Scroll the desktop thumbnail track (prev / next).
   *
   * @param {'previous' | 'next'} direction
   * @param {Event} [event]
   */
  nudgeDesktopThumbnailRail(direction, event) {
    event?.preventDefault();

    const strip = this.querySelector('[data-desktop-product-thumbnails]');
    const track = strip?.querySelector('.product-media-gallery__desktop-thumbnails-track');
    if (!(track instanceof HTMLElement)) return;

    this.#scrollThumbnailTrackBy(track, direction);
  }

  /**
   * Desktop rail lists all product media; slideshow may be variant-filtered.
   *
   * @param {string | number} mediaId
   * @param {Event} [event]
   */
  selectRailMedia(mediaId, event) {
    const id = String(mediaId);
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.refs?.slides;

    if (slides?.length) {
      for (let i = 0; i < slides.length; i++) {
        const slideMediaId = slides[i].querySelector('[data-media-id]')?.getAttribute('data-media-id');
        if (slideMediaId === id) {
          slideshow.select(i, event);
          return;
        }
      }
    }

    const target = event?.target;
    const btn = target instanceof Element ? target.closest('[data-media-id]') : null;
    const variantId = btn?.getAttribute('data-variant-id');
    if (!variantId || !btn) return;

    const mount = this.#galleryMountRoot();
    if (!mount) return;

    mount.dataset.pendingGalleryMediaId = id;
    mount.dataset.pendingGalleryVariantId = String(variantId);

    const radio = mount.querySelector(
      `variant-picker input[type="radio"][data-variant-id="${variantId}"]`
    );
    if (radio instanceof HTMLInputElement) {
      if (!radio.checked) radio.click();
      return;
    }

    const selectEl = mount.querySelector('variant-picker select.variant-option__select');
    if (selectEl instanceof HTMLSelectElement) {
      const opt = selectEl.querySelector(`option[data-variant-id="${variantId}"]`);
      if (opt instanceof HTMLOptionElement) {
        selectEl.value = opt.value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * After variant HTML swap, jump to the thumbnail the user chose (if still the active variant).
   * @returns {boolean} True if slide selection was scheduled async (rail scroll handled there).
   */
  #applyPendingGalleryMediaSelection() {
    const mount = this.#galleryMountRoot();
    const pendingMedia = mount?.dataset.pendingGalleryMediaId;
    const pendingVariant = mount?.dataset.pendingGalleryVariantId;

    if (!mount || !pendingMedia || !pendingVariant) return false;

    const form =
      mount.querySelector('form[action*="/cart/add"]') ||
      mount.querySelector('product-form form') ||
      mount.querySelector('form');

    const currentVariantId = form?.querySelector('input[name="id"]')?.value;

    delete mount.dataset.pendingGalleryMediaId;
    delete mount.dataset.pendingGalleryVariantId;

    if (String(currentVariantId) !== String(pendingVariant)) return false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#selectSlideByMediaId(pendingMedia));
    });
    return true;
  }

  /**
   * @param {string} mediaId
   */
  #selectSlideByMediaId(mediaId) {
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slideshow || !slides?.length) return;

    const id = String(mediaId);
    for (let i = 0; i < slides.length; i++) {
      const slideMediaId = slides[i].querySelector('[data-media-id]')?.getAttribute('data-media-id');
      if (slideMediaId === id) {
        slideshow.select(i, undefined, { animate: false });
        this.#updateThumbnailRailsSelectionByMediaId(id);
        this.#scrollThumbnailRailsToMediaId(id, 'instant');
        return;
      }
    }
  }

  /**
   * Keeps thumbnail rails in sync with the main slideshow (swipe, arrows, variant HTML swap).
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!(event instanceof SlideshowSelectEvent)) return;
    if (event.target !== this.refs.slideshow) return;

    const { index, trigger } = event.detail;
    const slides = this.refs.slideshow?.refs?.slides;
    const slide = slides?.[index];
    const mediaId = slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id');

    if (mediaId) this.#updateThumbnailRailsSelectionByMediaId(mediaId);

    if (!mediaId) return;

    const behavior =
      trigger === 'scroll' || prefersReducedMotion() ? 'instant' : 'smooth';

    this.#scrollThumbnailRailsToMediaId(mediaId, behavior);

    requestAnimationFrame(() => this.#syncVerticalThumbnailRailHeight());
  };

  #syncThumbnailRailsFromSlideshow() {
    const slideshow = this.refs.slideshow;
    if (!slideshow || this.#thumbnailRailStrips().length === 0) return;

    const slides = slideshow.refs?.slides;
    const slide = slides?.[slideshow.current];
    const mediaId = slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id');

    if (mediaId) this.#updateThumbnailRailsSelectionByMediaId(mediaId);
  }

  /**
   * @param {string} mediaId
   */
  #updateThumbnailRailsSelectionByMediaId(mediaId) {
    const id = String(mediaId);
    for (const strip of this.#thumbnailRailStrips()) {
      strip.querySelectorAll('[data-media-id]').forEach((btn) => {
        btn.setAttribute('aria-selected', btn.getAttribute('data-media-id') === id ? 'true' : 'false');
      });
    }
  }

  /**
   * @param {HTMLElement} strip
   * @param {string} mediaId
   * @param {'smooth' | 'instant'} behavior
   */
  #scrollThumbnailRailStripToMediaId(strip, mediaId, behavior) {
    if (mediaId == null) return;

    const { width, height } = strip.getBoundingClientRect();
    if (width === 0 && height === 0) return;

    const id = String(mediaId);
    const active = [...strip.querySelectorAll('[data-media-id]')].find(
      (b) => b.getAttribute('data-media-id') === id
    );
    if (!active) return;

    const scrollBehavior =
      behavior === 'smooth' && prefersReducedMotion() ? 'instant' : behavior;

    const vertical = Boolean(strip.querySelector('.product-media-gallery__desktop-thumbnails-track--vertical'));

    active.scrollIntoView({
      inline: vertical ? 'nearest' : 'center',
      block: vertical ? 'center' : 'nearest',
      behavior: scrollBehavior,
    });
  }

  /**
   * @param {string} mediaId
   * @param {'smooth' | 'instant'} behavior
   */
  #scrollThumbnailRailsToMediaId(mediaId, behavior) {
    for (const strip of this.#thumbnailRailStrips()) {
      this.#scrollThumbnailRailStripToMediaId(strip, mediaId, behavior);
    }
  }

  /**
   * @param {'smooth' | 'instant'} behavior
   */
  #scrollRailsToCurrentSlide(behavior) {
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.refs?.slides;
    const slide = slides?.[slideshow?.current ?? 0];
    const mediaId = slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    if (mediaId) this.#scrollThumbnailRailsToMediaId(mediaId, behavior);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
